import React, { useEffect, useState } from 'react';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Checkbox } from '../Checkbox/Checkbox';
import './Table.scss';

interface Column {
  id: string;
  label: string;
  width?: string;
  render?: (
    row: any,
    onDelete?: (id: number) => void,
    onDownload?: (id: number) => void,
    isShowAddNewResourceButton?: boolean,
    onEdit?: (id: number) => void
  ) => React.ReactNode;
}

interface TableProps {
  columns: Column[];
  data: any[];
  onRowSelect?: (selectedRows: any[]) => void;
  onDelete?: (id: number) => void;
  onDownload?: (id: number) => void;
  onEdit?: (id: number) => void;
  isShowAddNewResourceButton?: boolean;
}

export const Table: React.FC<TableProps> = ({
  columns,
  data,
  onRowSelect,
  onDelete,
  onDownload,
  onEdit,
  isShowAddNewResourceButton,
}) => {
  const [selected, setSelected] = useState<number[]>([]);
  const hasSelection = !!onRowSelect;

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!onRowSelect) return;
    const newSelected = event.target.checked ? data.map((item) => Number(item.id)) : [];

    setSelected(newSelected);
    onRowSelect?.(data.filter((row) => newSelected.includes(Number(row.id))));
  };

  const handleSelectRow = (rowId: number) => {
    if (!onRowSelect) return;
    const newSelected = selected.includes(rowId)
      ? selected.filter((id) => id !== rowId)
      : [...selected, rowId];

    setSelected(newSelected);
    onRowSelect?.(data.filter((row) => newSelected.includes(Number(row.id))));
  };

  const isSelected = (rowId: number) => selected.includes(rowId);

  useEffect(() => {
    if (data.some((item) => !item.id)) {
      console.error('Data items must have unique "id" property');
    }
    setSelected([]);
    onRowSelect?.([]);
  }, [data, onRowSelect]);

  return (
    <TableContainer className="table">
      <MuiTable>
        <TableHead>
          <TableRow>
            {hasSelection && (
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < data.length}
                  checked={data.length > 0 && selected.length === data.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
            )}
            {columns.map((column) => (
              <TableCell key={column.id} style={{ width: column.width }}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => {
            const isItemSelected = isSelected(Number(row.id));

            return (
              <TableRow
                hover
                onClick={(e) => {
                  if (!hasSelection) return;
                  const target = e.target as Element;
                  if (target.closest('.action-icons')) {
                    e.stopPropagation();
                    return;
                  }
                  if (target.closest('.thumbnail-cell')) return;
                  handleSelectRow(Number(row.id));
                }}
                role={hasSelection ? 'checkbox' : undefined}
                aria-checked={hasSelection ? isItemSelected : undefined}
                tabIndex={hasSelection ? -1 : undefined}
                key={row.id}
                selected={hasSelection && isItemSelected}
              >
                {hasSelection && (
                  <TableCell padding="checkbox">
                    <Checkbox checked={isItemSelected} />
                  </TableCell>
                )}
                {columns.map((column) => {
                  const isTooltipColumn = ['office', 'area', 'agency'].includes(column.id);
                  const defaultContent = row[column.id];
                  const content = column.render
                    ? column.render(
                        row,
                        onDelete || (() => {}),
                        onDownload,
                        isShowAddNewResourceButton,
                        onEdit
                      )
                    : defaultContent;
                  return (
                    <TableCell key={column.id} style={{ width: column.width }}>
                      {column.render ? (
                        content
                      ) : (
                        <span
                          className="cell-text"
                          title={
                            isTooltipColumn && defaultContent ? String(defaultContent) : undefined
                          }
                        >
                          {content}
                        </span>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};
